import { z } from "zod";

import {
  DisplayUomOverrideSchema,
  DocumentDisplayUomSettingsSchema,
  emptyDocumentDisplayUomSettings,
} from "~/shared/documents/schemas/document-display-uom.schema";

/** Org key INVOICE_DOCUMENT_CONFIG — invoice + proforma PDF options (SC-2494). */
export const INVOICE_DOCUMENT_CONFIG_VERSION = 1;

export const InvoicePricingDisplaySchema = z.enum(["PACKAGE_AND_UOM", "UOM_ONLY", "PACKAGE_ONLY"]);
export type InvoicePricingDisplay = z.infer<typeof InvoicePricingDisplaySchema>;

export const InvoicePricingDisplayOverrideSchema = InvoicePricingDisplaySchema.nullish();

export const InvoiceDocumentConfigSchema = z
  .object({
    configVersion: z.literal(INVOICE_DOCUMENT_CONFIG_VERSION).default(INVOICE_DOCUMENT_CONFIG_VERSION),
    hideWarehouseOnDirectShipOrders: z.boolean().default(false),
    pricingDisplay: InvoicePricingDisplaySchema.default("PACKAGE_AND_UOM"),
    allowPricingDisplayOverrideOnCreate: z.boolean().default(false),
  })
  .merge(DocumentDisplayUomSettingsSchema);

export type InvoiceDocumentConfig = z.infer<typeof InvoiceDocumentConfigSchema>;

export const InvoiceDocumentPdfInputSchema = z.object({
  pricingDisplayOverride: InvoicePricingDisplayOverrideSchema,
  displayUomOverride: DisplayUomOverrideSchema,
});

export type InvoiceDocumentPdfInput = z.infer<typeof InvoiceDocumentPdfInputSchema>;

/** Fields exposed to authenticated create-modal callers (no settings-admin permission required). */
export const InvoiceDocumentConfigForCreateModalSchema = InvoiceDocumentConfigSchema.pick({
  displayUom: true,
  allowDisplayUomOverrideOnCreate: true,
  pricingDisplay: true,
  allowPricingDisplayOverrideOnCreate: true,
});
export type InvoiceDocumentConfigForCreateModal = z.infer<typeof InvoiceDocumentConfigForCreateModalSchema>;

export const pickInvoiceDocumentConfigForCreateModal = (config: InvoiceDocumentConfig): InvoiceDocumentConfigForCreateModal =>
  InvoiceDocumentConfigForCreateModalSchema.parse(config);

export const emptyInvoiceDocumentConfig = (): InvoiceDocumentConfig => ({
  configVersion: INVOICE_DOCUMENT_CONFIG_VERSION,
  hideWarehouseOnDirectShipOrders: false,
  pricingDisplay: "PACKAGE_AND_UOM",
  allowPricingDisplayOverrideOnCreate: false,
  ...emptyDocumentDisplayUomSettings(),
});

export const tryParseInvoiceDocumentConfigString = (raw: string): InvoiceDocumentConfig | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    const result = InvoiceDocumentConfigSchema.safeParse(parsed);
    return result.success ? result.data : null;
  } catch {
    return null;
  }
};

export const parseInvoiceDocumentConfigForSettings = (raw: string | null | undefined): InvoiceDocumentConfig => {
  if (!raw?.trim()) {
    return emptyInvoiceDocumentConfig();
  }
  const parsed = tryParseInvoiceDocumentConfigString(raw);
  if (!parsed) {
    throw new Error("Invoice document settings could not be read. Contact support before saving.");
  }
  return parsed;
};

export const serializeInvoiceDocumentConfig = (config: InvoiceDocumentConfig): string =>
  JSON.stringify(InvoiceDocumentConfigSchema.parse(config));

export const assertInvoiceDocumentOrgSettingValueForWrite = (value: string): void => {
  if (!value?.trim()) {
    return;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Invalid invoice document config JSON");
  }
  const result = InvoiceDocumentConfigSchema.safeParse(parsed);
  if (!result.success) {
    throw new Error("Invalid invoice document config schema");
  }
};

export const resolveInvoicePricingDisplay = (
  orgConfig: Pick<InvoiceDocumentConfig, "pricingDisplay" | "allowPricingDisplayOverrideOnCreate">,
  modalOverride?: InvoicePricingDisplay | null,
): InvoicePricingDisplay => {
  const effectiveOverride = orgConfig.allowPricingDisplayOverrideOnCreate ? modalOverride : null;
  return effectiveOverride ?? orgConfig.pricingDisplay;
};
